import { useState, useContext } from 'react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { RTPFilterForm } from './RTPFilterForm';
import { RTPDownloadActions } from './RTPDownloadActions';
import { RTPPreviewTable } from './RTPPreviewTable';
import { AuthContext } from '@/contexts';
import { downloadPatientRTP } from '@/api/rtpDownload';
import { downloadZip, generateDownloadFilename } from '@/utils/download';
import { createRtpCsvZip } from '@/utils/zipDownload';
import { useRTPDownloadConfig } from '@/hooks/useRTPDownloadConfig';
import { useRTPPreviewDownload } from '@/hooks/useRTPPreviewDownload';
import { useToast } from '@/contexts/toastContextBase';
import { getPatientFullName } from '@/types/patient';
import type {
  DownloadProgress,
  RTPDownloadResult,
} from '@/types/rtpDownload';

export function PatientRTPDownloadBuilder() {
  const authContext = useContext(AuthContext);
  const { showToast } = useToast();

  const {
    timeRange,
    grouping,
    onTimeRangeChange,
    onGroupingChange,
    hasTimeRange,
  } = useRTPDownloadConfig();

  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>({
    status: 'idle',
  });

  const patient = authContext?.user
    ? {
        id: authContext.user.id,
        username: authContext.user.username,
        firstName: authContext.user.firstName || authContext.user.username,
        lastName: authContext.user.lastName || '',
        middleName: undefined,
        dateOfBirth: undefined,
        apiToken: undefined,
        activities: [],
        totalActivities: 0,
        status: 'active' as const,
      }
    : null;

  const patients = patient ? [patient] : [];

  const {
    cachedResults,
    previewData,
    previewState,
    selectedPreviewPatientIds,
    setSelectedPreviewPatientIds,
    loadPreview,
    isPreviewStale,
  } = useRTPPreviewDownload({
    timeRange,
    grouping,
    patientIds: patient ? [patient.id] : [],
    fetchResults: async () => {
      if (!patient) return new Map();

      const result = await downloadPatientRTP(
        {
          timeRange: {
            from: timeRange.from!,
            to: timeRange.to!,
          },
          grouping,
        },
        patient.id,
        getPatientFullName(patient)
      );

      return new Map([[patient.id, result]]);
    },
  });

  const handlePreview = async () => {
    if (!hasTimeRange) {
      showToast('error', 'Please select a time range');
      return;
    }

    if (!patient) {
      showToast('error', 'User not authenticated');
      return;
    }

    try {
      const { count } = await loadPreview();
      if (count === 0) {
        showToast('warning', 'No RTP data found for this time range');
      } else {
        showToast('success', 'Preview loaded');
      }
    } catch {
      showToast('error', 'Failed to load preview');
    }
  };

  const handleDownload = async () => {
    if (!cachedResults || selectedPreviewPatientIds.size === 0) {
      showToast('error', 'Please preview and select patients first');
      return;
    }

    if (isPreviewStale()) {
      showToast('error', 'Preview data is outdated. Please refresh preview.');
      return;
    }

    try {
      setDownloadProgress({ status: 'processing', message: 'Preparing download...' });

      const selectedResults: RTPDownloadResult[] = [];
      selectedPreviewPatientIds.forEach((patientId) => {
        const result = cachedResults.get(patientId);
        if (result) {
          selectedResults.push(result);
        }
      });

      const zipBlob = await createRtpCsvZip(selectedResults);
      const filename = generateDownloadFilename('rtp-download', 'zip');
      downloadZip(zipBlob, filename);

      setDownloadProgress({ status: 'complete' });
      showToast('success', 'RTP data downloaded successfully');

      setTimeout(() => {
        setDownloadProgress({ status: 'idle' });
      }, 3000);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to download RTP data';
      setDownloadProgress({ status: 'error', message });
      showToast('error', message);

      setTimeout(() => {
        setDownloadProgress({ status: 'idle' });
      }, 5000);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Configure Download"
          description="Select time range and grouping for your RTP data"
        />
        <CardBody>
          <RTPFilterForm
            timeRange={timeRange}
            grouping={grouping}
            onTimeRangeChange={onTimeRangeChange}
            onGroupingChange={onGroupingChange}
          />
        </CardBody>
      </Card>

      {previewData && previewData.size > 0 && (
        <Card>
          <CardHeader
            title="Preview Results"
            description={`${previewData.size} patient with RTP data found`}
          />
          <CardBody>
            <RTPPreviewTable
              previewData={previewData}
              patients={patients}
              selectedPatientIds={selectedPreviewPatientIds}
              onTogglePatient={(id) => {
                const newSet = new Set(selectedPreviewPatientIds);
                if (newSet.has(id)) {
                  newSet.delete(id);
                } else {
                  newSet.add(id);
                }
                setSelectedPreviewPatientIds(newSet);
              }}
              onSelectAll={() => {
                setSelectedPreviewPatientIds(new Set(previewData.keys()));
              }}
              onDeselectAll={() => {
                setSelectedPreviewPatientIds(new Set());
              }}
            />
          </CardBody>
        </Card>
      )}

      <RTPDownloadActions
        hasPreviewData={cachedResults !== null}
        isLoadingPreview={previewState.isLoading}
        previewError={previewState.error}
        onPreview={handlePreview}
        canPreview={hasTimeRange}
        selectedPreviewPatientIds={selectedPreviewPatientIds}
        onDownload={handleDownload}
        isDownloading={downloadProgress.status === 'processing'}
        isPreviewStale={isPreviewStale()}
        timeRange={timeRange}
        grouping={grouping}
        patientCount={patients.length}
      />
    </div>
  );
}
